//@flow

import React from 'react';
import './NotLoggedBlock.css';
import { togglePageLoadPlaceholder } from '../util/DOMUtil';
import type { AuthType } from '../auth/AuthType';
import type { AuthResultEvent } from '../auth/AuthResultEvent';
import ErrorId from '../protocol/ErrorId';
import Authenticator2 from '../auth/Authenticator2';
import Store from '../store/Store';
import StoreDataChangeEvent from '../store/StoreDataChangeEvent';
import './OAuthNotLoggedInBlock.css';
import I18nPopover from './I18nPopover';
import t from './../i18n/I18n';
import { ThemeSetup } from '../themes/ThemeSetup';
import { openURL } from '../util/ExtUtil';
import System from '../basic/System';
import AuthMechanism from '../auth/AuthMechanism';
import Modal from './../modals/Modal2';
import _ from 'lodash';
import ThumbnailUtil from '../util/ThumbnailUtil';
import Button from '../common/Button';
import NotLoggedBlock from './NotLoggedBlock';
import { number } from 'prop-types';

const debug = require('../debug/debug')('wc:login:OAuthNotLoggedInBlock');

type Props = {
  fullHeight: ?boolean,
  toggleSpinner: ?boolean,
  onAuthMechanismChange: (authMechanism: AuthMechanism) => void
};

type State = {
  authResultEvent: ?AuthResultEvent,
  isRequesting: ?boolean,
  counter: ?number,
  errorMsg: ?string,
  errorMsgDetail: ?string,
  globalLogoutUrl: ?string,
  basicAuthMechanismAvailable?: boolean,
  microsoftLoginURL: ?string
};

export default class OAuthNotLoggedInBlock extends React.Component<Props, State> {
  onAuthResultEventBinded: Function;
  onLogoutUrlChangeBinded: Function;
  loginUrl: ?string;
  logoutButtonHideTimerId: ?any; //Using any, because timer-id returned by setTimeOut is of nodejs-timeout.

  constructor(props: Props) {
    super(props);
    if (debug.enabled) {
      debug('[new OAuthNotLoggedInBlock(props: %0)]', this.props);
    }
    this.state = {
      isRequesting: false,
      authResultEvent: null,
      errorMsg: null,
      errorMsgDetail: null,
      counter: 0,
      globalLogoutUrl: null,
      microsoftLoginURL: null
    };
    //Need to bind this specific method, becoz without doing, I am not able to access to its instance internally - Om
    this.onAuthResultEventBinded = this.onAuthResultEvent.bind(this);
    this.onLogoutUrlChangeBinded = this.onLogoutUrlChange.bind(this);
    this.loginUrl = null;
  }

  //variable for hold opened logined window refs
  _openedAuthWindowRef: window = null;

  static defaultProps = {
    backgroundImage: 'url(images/login_background.jpg)',
    subtitle: 'PAGE_REQUIRES_LOGIN',
    fullHeight: true,
    toggleSpinner: true,
    resetSessionOnLoginFail: true,
    isLinkUser: false
  };

  getFirstAuthType = (): AuthType => {
    const authTypes = Authenticator2.findOAuthType();
    return authTypes && authTypes.length > 0 ? authTypes[0] : null;
  };

  componentDidMount() {
    if (this.props.toggleSpinner) {
      togglePageLoadPlaceholder(true);
    }
    if (debug.enabled) {
      debug('[componentDidMount()] Registering for auth result event.');
    }
    window.gs.updateNotLoggedInBlock = () => {
      this.forceUpdate();
    };
    //Note: Subscribe will fire the event immediately if data is present on it.
    Store.subscribe(
      Authenticator2.AUTH_STORE_NAME,
      this.onAuthResultEventBinded,
      Authenticator2.OAUTH_RESPONSE_DATA_EVENT
    );
    Store.subscribe(Authenticator2.AUTH_STORE_NAME, this.onLogoutUrlChangeBinded, Authenticator2.GLOBAL_LOGOUT_URL);
    this.setState({ basicAuthMechanismAvailable: Authenticator2.isBasicMechanismAvailable() });
  }

  componentWillUnmount() {
    window.gs.updateNotLoggedInBlock = null;
    if (this.props.toggleSpinner) {
      togglePageLoadPlaceholder(false);
    }
    if (debug.enabled) {
      debug('[componentWillUnmount()] Un-Registering for auth result event. ');
    }
    Store.unsubscribe(
      Authenticator2.AUTH_STORE_NAME,
      this.onAuthResultEventBinded,
      Authenticator2.OAUTH_RESPONSE_DATA_EVENT
    );
    Store.unsubscribe(Authenticator2.AUTH_STORE_NAME, this.onLogoutUrlChangeBinded, Authenticator2.GLOBAL_LOGOUT_URL);
    if (this.logoutButtonHideTimerId) {
      clearTimeout(this.logoutButtonHideTimerId);
    }
  }

  onLogoutUrlChange(event: StoreDataChangeEvent<string>): void {
    if (debug.enabled) {
      debug('[onLogoutUrlChange(event.name:%s, event.data: %O)].', event.getName(), event.data);
    }
    if (this.state.globalLogoutUrl !== event.data) {
      this.setState({ globalLogoutUrl: event.data });
    }
    if (event.data && event.data.length > 0) {
      //this.startAutoHideTimerForLogoutLink();
    }
  }

  onAuthResultEvent(event: StoreDataChangeEvent<AuthResultEvent>) {
    if (debug.enabled) {
      debug('[onAuthResultEvent(event.name:%s, event.data: %O)].', event.getName(), event.data);
    }
    const data: ?AuthResultEvent = event.data;
    if (data) {
      if (data.url) {
        if (!this.loginUrl) {
          this.loginUrl = data.url;
          this.openLoginWindow();
        }
      } else if (!data.success) {
        let errorId: ErrorId = null;
        if (data.successMessage) {
          errorId = new ErrorId('ERROR', data.successMessage, null, data.progressMessage);
        } else {
          errorId = new ErrorId(
            'ERROR',
            t('LOGIN.LOGIN_REQUEST_FAILED'),
            'LOGIN.LOGIN_REQUEST_FAILED',
            data.progressMessage
          );
        }
        this.onLoginError(errorId);
      }
    }

    //this.setState({ authResultEvent: data });
  }

  login = () => {
    if (debug.enabled) {
      debug('[login()] Going to login...');
    }

    const authType: AuthType = this.getFirstAuthType();
    if (!authType) {
      if (debug.enabled) {
        debug('[login()] No AuthType available...');
      }
      return;
    }
    this.setState({ isRequesting: true, errorMsg: null, errorMsgDetail: null });
    window.gs.logoutGlobalURL = null;
    const promise: Promise<AuthResultEvent> = authType.login();
    promise.then(this.onLoginResult, this.onLoginError);
  };

  onLoginResult = (loginResult: AuthResultEvent) => {
    if (debug.enabled) {
      debug('[onLoginResult(loginResult = %O)]', loginResult);
    }
    if (loginResult.url) {
      let tmpURl = loginResult.url;
      this.openNewWindowWithUrl(tmpURl);
    }
  };

  onLoginError = (errorErrorId: ErrorId) => {
    if (debug.enabled) {
      debug('[onLoginError(errorId = %O)]', errorErrorId);
    }
    this.setState({
      isRequesting: false,
      errorMsg: errorErrorId.toString(),
      errorMsgDetail: errorErrorId.backendError
    });
    this.loginUrl = null;
    //Todo: Show login error.
  };

  openLoginWindow(): void {
    if (this.loginUrl) {
      this.openNewWindowWithUrl(this.loginUrl);
    }
  }

  cancel = () => {
    const authType: AuthType = this.getFirstAuthType();
    if (authType) {
      authType.abort();
    } else if (debug.enabled) {
      debug('[cancel()] No AuthType available...');
    }
    this.setState({ isRequesting: false, errorMsg: null, errorMsgDetail: null });
    this.loginUrl = null;
    this.closeNewWindow();
    //Todo: Set state to canceled and show login again.
  };

  willOpenWithButton(): boolean {
    return this.isSafariBrowser();
  }

  isSafariBrowser(): boolean {
    let isSafari =
      window.safari ||
      (navigator.vendor &&
        navigator.vendor.indexOf('Apple') > -1 &&
        navigator.userAgent &&
        navigator.userAgent.indexOf('CriOS') == -1 &&
        navigator.userAgent.indexOf('FxiOS') == -1);
    return isSafari ? true : false;
  }

  //Function for open auth url window
  openNewWindowWithUrl(url: string) {
    if (debug.enabled) {
      const authType: AuthType = this.getFirstAuthType();
      debug('[openNewWindowWithUrl(url:%s, realmName: %s:)]', url, authType ? authType.getAuthRealm().name : null);
    }
    if (this.willOpenWithButton()) {
      this.setState({ microsoftLoginURL: url });
    } else {
      this._openedAuthWindowRef = openURL(url, '_blank'); // more consistent with other apps, unlike: open(url, realmName, 'height=800,width=800');
    }
  }

  onClose = () => {
    this.setState({ microsoftLoginURL: null });
    this.cancel();
  };

  openMicrosoftWindow = (url: string) => {
    this.setState({ microsoftLoginURL: null });
    this._openedAuthWindowRef = openURL(this.state.microsoftLoginURL, '_blank');
  };

  renderConfirmationModal = () => {
    const modalProps = {
      name: 'ConfirmOpenTab',
      title: t('CONFIRM'),
      onClose: this.onClose,
      buttons: [
        { label: t('NO'), onClick: this.onClose, type: 'CANCEL' },
        { label: t('YES'), onClick: this.openMicrosoftWindow, type: 'PRIMARY' }
      ]
    };

    return (
      <Modal {...modalProps} closeOnEsc>
        <div>
          <div>{t('LOGIN.PROCEED_WITH_OAUTH')}</div>
        </div>
      </Modal>
    );
  };

  //Function for close opened window
  closeNewWindow() {
    if (this._openedAuthWindowRef) this._openedAuthWindowRef.close();
  }

  onLogoutAnchorClick = () => {
    this.openNewWindowWithUrl(this.state.globalLogoutUrl);
    this.resetLogoutUrl();
  };

  resetLogoutUrl = () => {
    if (debug.enabled) {
      debug('[resetLogoutUrl()] Redirecting to global logout URL...', window.gs.globalLogoutURL);
    }
    Authenticator2.AUTH_STORE.put(Authenticator2.GLOBAL_LOGOUT_URL, null);
    //Store.put(OAUTH_LOGOUT_STATE, false);
    //clearTimeout(this._logoutTimer);
    //window.open(Authenticator2.AUTH_STORE.get(Authenticator2.GLOBAL_LOGOUT_URL), '_blank');
  };

  startAutoHideTimerForLogoutLink = () => {
    if (debug.enabled) {
      debug('hideLogoutFromSessions()] hiding logout from all sign-on sessions button after 3 minutes');
    }
    this.logoutButtonHideTimerId = setTimeout(this.resetLogoutUrl, 10000);
  };

  renderLogoutFromAllSessions = () => {
    if (this.state.globalLogoutUrl != null) {
      return (
        <div style={styles.clickHere}>
          <Button type="LINK" text={t('LOGIN.CLICK_HERE')} onClick={this.onLogoutAnchorClick}></Button>
          &nbsp;{t('LOGIN.LOGOUT_FROM_ALL_SIGN_ON_SESSIONS')}
        </div>
      );
    } else {
      return null;
    }
    /* return (
      <div>{logoutState ? <div style={styles.clickHere}><a href="#" onClick={this.onLogOutOfAllSessions}>{window.lang.CLICK_HERE()}</a>
        &nbsp;{window.lang.LOGOUT_FROM_ALL_SIGN_ON_SESSIONS()}</div> : null}</div>
    ); */
  };

  renderLoginAndCancelButton = () => {
    const authType: AuthType = this.getFirstAuthType();
    const buttonStyle = {
      ...styles.button,
      backgroundColor: '#6174f2',
      color: 'white',
      opacity: 1
    };
    return (
      <div>
        {this.state.isRequesting ? (
          <div>
            {/* <center> <div className="spinner"></div></center> */}
            <div onClick={this.cancel} style={buttonStyle} ref="signInButton">
              <center>
                <div className="spinner loaderSpinner" />
                &nbsp;&nbsp;
                {t('CANCEL')}
              </center>
            </div>
          </div>
        ) : (
          <div onClick={this.login} style={buttonStyle} ref="signInButton">
            <img src="images/ping-fapro.svg" className="ping-fapro" />
            <p style={{ display: 'inline', paddingLeft: '6px' }}>
              {t('LOGIN.CONNECT_OAUTHTEXT')}{' '}
              {authType && authType.getAuthRealm().label
                ? authType.getAuthRealm().label
                : t('LOGIN.CONNECT_OAUTHTEXT_DEFAULT_REALM')}
            </p>
          </div>
        )}
      </div>
    );
  };

  renderI18nPopover = () => {
    return (
      <div className="I18nSelecter">
        <center>
          <I18nPopover />
        </center>
      </div>
    );
  };

  getLoginBackgroundImage() {
    return NotLoggedBlock.getLoginBackgroundImage();
  }

  getLogoByPosition = (position: number) => {
    if(System.isDesktop())  return window?.loginOverlay? window.loginOverlay[0][position-1] : null;
    else return ThumbnailUtil.getLoginBackgroundLogos(position);
  }

  renderSupportedLogo = (url: string) => <img src={url} onError={e=> e.target.style.visibility = 'hidden'}/>
  

  render() {
    const fullHeight = this.props.fullHeight;
    const backgroundImage = window.loginBgExists
      ? `url(${this.getLoginBackgroundImage()}`
      : 'url(images/login_background.jpg)';
    return (
      <div name="OAuthNotLoggedBlock" style={{ backgroundImage }}>
        <div className="area area-top-left">
        {this.renderSupportedLogo(this.getLogoByPosition(1))}
        </div>
        <div className="area area-top-mid">
          {this.renderSupportedLogo(this.getLogoByPosition(2))}
          </div>
        <div className="area area-top-right">
        {this.renderSupportedLogo(this.getLogoByPosition(3))}
        </div>
        <div className="area area-mid-left">
          {this.renderSupportedLogo(this.getLogoByPosition(4))}
          </div>
        <div className="area area-mid-mid flex-col">
            <div className="pane">
              <div
                style={{
                  height: '40%',
                  backgroundImage: ThemeSetup.getBackgroundURL('lock.svg'),
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center'
                }}
              />
              <div className="header">{System.isBrowserApp() ? t('WELCOME') : t('WELCOME_DESKTOP')}</div>
              <div className="subtitle">{t('LOGIN.PAGE_REQUIRES_LOGIN')}</div>
              <div style={{ color: '#ec7568' }}>
                <span title={this.state.errorMsgDetail ? this.state.errorMsgDetail : ''}>{this.state.errorMsg}</span>
              </div>
              {this.state.microsoftLoginURL ? this.renderConfirmationModal() : null}
              {this.renderLoginAndCancelButton()}
              {this.renderLogoutFromAllSessions()}
              {this.renderLoginWithUserNamePasswordLink()}
            </div>
            {this.renderI18nPopover()}
        </div>
        <div className="area area-mid-right">
          {this.renderSupportedLogo(this.getLogoByPosition(6))}
          </div>
        <div className="area area-bottom-left">
        {this.renderSupportedLogo(this.getLogoByPosition(7))}
        </div>
        <div className="area area-bottom-mid">
          {this.renderSupportedLogo(this.getLogoByPosition(8))}
          </div>
        <div className="area area-bottom-right">
        {this.renderSupportedLogo(this.getLogoByPosition(9))}
        </div>
      </div>
    );
  }

  renderLoginWithUserNamePasswordLink() {
    if (this.state.basicAuthMechanismAvailable === true && !System.isDesktop()) {
      return (
        <div style={styles.clickHere}>
          <a href="#" onClick={this.switchToBasicAuthMechanism}>
            {t('LOGIN.LOGIN_WITH_USERNAME_PASSWORD')}
          </a>
        </div>
      );
    } else {
      return null;
    }
  }

  switchToBasicAuthMechanism = (e: Event) => {
    e.preventDefault();
    this.props.onAuthMechanismChange(AuthMechanism.Basic);
  };
}

const styles = {
  forgetLink: {
    height: '18px',
    color: '#7A7A7A',
    fontSize: '14px',
    lineHeight: '18px',
    textAlign: 'center',
    cursor: 'pointer',
    margin: '20px 0',
    letterSpacing: '1px',
    borderRadius: '2px',
    width: '160px'
  },
  otp: {
    height: '18px',
    color: '#7A7A7A',
    fontSize: '14px',
    lineHeight: '18px',
    textAlign: 'center',
    cursor: 'pointer',
    margin: '20px 0 20px 10px',
    letterSpacing: '1px',
    borderRadius: '2px'
  },
  button: {
    textAlign: 'center',
    cursor: 'pointer',
    borderRadius: '2px',
    padding: '10px',
    marginBottom: '10px',
    marginTop: '8px',
    fontSize: '12px',
    letterSpacing: '1px',
    fontWeight: 600
  },
  clickHere: {
    fontSize: '13px',
    textAlign: 'center',
    margin: '8px',
    color: 'var(--primary-font-color)'
  }
};
